import React from "react";
import {Section} from "../../../../../elements/section/Section";
import {CustomSwitchFieldWithTitle} from "../../../../../elements/customSwitchFieldWithTitle/CustomSwitchFieldWithTitle";
import {useAppDispatch, useAppSelector} from "../../../../../../app/hooks";
import {
    updateSettings,
    selectRequestsConfiguration,
    selectBookingConfigs,
    selectCallbackBookingConfig,
    selectTestDriveBookingConfig
} from "../toolSettingsSlice";
import {BookingType, BotType, RequestMode, UpdateBookingConfigRequest} from "../../../../../../api/generated";
import {DisabledField, isFieldDisabled} from "../../../assistantPreset";
import {SelectDropdown, SelectOption} from "../../../../../elements/select/SelectDropdown";
import {DurationInputField} from "../../../../../elements/durationInputField/DurationInputField";
import {SecondaryCollapsibleSection} from "../../../../../elements/collapsableSection/secondary/SecondaryCollapsibleSection";
import styles from "../ToolSettingsForm.module.css";

interface RequestSettingsProps {
    botType: BotType;
}

const modeOptions: SelectOption<RequestMode>[] = [
    {label: "Request", value: RequestMode.Request},
    {label: "Booking", value: RequestMode.Booking}
];

const defaultBookingConfigs: Record<BookingType, UpdateBookingConfigRequest> = {
    [BookingType.CallBack]: {
        bookingType: BookingType.CallBack,
        bookingStepSeconds: 15 * 60,
        timeToCloseSeconds: 30 * 60,
        minimumNoticeSeconds: 30 * 60
    },
    [BookingType.TestDrive]: {
        bookingType: BookingType.TestDrive,
        bookingStepSeconds: 30 * 60,
        timeToCloseSeconds: 60 * 60,
        minimumNoticeSeconds: 24 * 60 * 60
    }
};

export const RequestSettings: React.FC<RequestSettingsProps> = ({botType}) => {
    const dispatch = useAppDispatch();
    const requestsConfiguration = useAppSelector(selectRequestsConfiguration);
    const bookingConfigs = useAppSelector(selectBookingConfigs);
    const callbackBookingConfig = useAppSelector(selectCallbackBookingConfig);
    const testDriveBookingConfig = useAppSelector(selectTestDriveBookingConfig);

    const isTestDriveDisabled = isFieldDisabled(DisabledField.TEST_DRIVE_REQUEST, botType);

    const updateRequestsConfiguration = (field: Partial<typeof requestsConfiguration>) => {
        dispatch(updateSettings({
            requestsConfiguration: {
                ...requestsConfiguration,
                ...field
            }
        }));
    };

    const updateBookingConfig = (
        bookingType: BookingType,
        field: keyof Pick<UpdateBookingConfigRequest, "bookingStepSeconds" | "timeToCloseSeconds" | "minimumNoticeSeconds">,
        value: number
    ) => {
        const nextConfigs = [...bookingConfigs];
        const configIndex = nextConfigs.findIndex(config => config.bookingType === bookingType);
        const currentConfig = configIndex >= 0 ? nextConfigs[configIndex] : defaultBookingConfigs[bookingType];
        const updatedConfig = {
            ...currentConfig,
            [field]: value
        };

        if (configIndex >= 0) {
            nextConfigs.splice(configIndex, 1, updatedConfig);
        } else {
            nextConfigs.push(updatedConfig);
        }

        dispatch(updateSettings({bookingConfigs: nextConfigs}));
    };

    const renderBookingConfig = (
        bookingType: BookingType,
        bookingConfig: UpdateBookingConfigRequest | undefined,
        disabled: boolean
    ) => {
        const config = bookingConfig || defaultBookingConfigs[bookingType];

        return (
            <div className={styles.fieldsLineWithMargin}>
                <DurationInputField
                    title="Booking step"
                    valueSeconds={config.bookingStepSeconds}
                    onChangeSeconds={(value) => updateBookingConfig(bookingType, "bookingStepSeconds", value)}
                    disabled={disabled}
                />
                <DurationInputField
                    title="Time to close"
                    valueSeconds={config.timeToCloseSeconds}
                    onChangeSeconds={(value) => updateBookingConfig(bookingType, "timeToCloseSeconds", value)}
                    disabled={disabled}
                />
                <DurationInputField
                    title="Minimum notice"
                    valueSeconds={config.minimumNoticeSeconds}
                    onChangeSeconds={(value) => updateBookingConfig(bookingType, "minimumNoticeSeconds", value)}
                    disabled={disabled}
                />
            </div>
        );
    };

    const isCallbackBookingConfigDisabled = !requestsConfiguration.callbackRequestsEnabled
        || requestsConfiguration.callbackMode !== RequestMode.Booking;
    const isTestDriveBookingConfigDisabled = isTestDriveDisabled
        || !requestsConfiguration.testDriveRequestsEnabled
        || requestsConfiguration.testDriveMode !== RequestMode.Booking;

    return (
        <Section title="Request & Booking Settings">
            <SecondaryCollapsibleSection title="Callback">
                <div className={styles.fieldsLine}>
                    <CustomSwitchFieldWithTitle
                        textOnTrue={"Enabled"}
                        textOnFalse={"Disabled"}
                        title={"Enable callback"}
                        description={"Enable customers to request or book callbacks through assistant"}
                        valueSelector={(state) => state.toolSettings.updateRequest.requestsConfiguration.callbackRequestsEnabled}
                        toggleValue={(v) => updateRequestsConfiguration({callbackRequestsEnabled: v})}
                    />
                </div>
                <div className={styles.fieldsLine}>
                    <SelectDropdown<RequestMode>
                        title="Mode"
                        value={requestsConfiguration.callbackMode}
                        handleChange={(value) => updateRequestsConfiguration({callbackMode: value})}
                        options={modeOptions}
                        selectFirstOptionByDefault={false}
                        disabled={!requestsConfiguration.callbackRequestsEnabled}
                    />
                </div>
                {renderBookingConfig(BookingType.CallBack, callbackBookingConfig, isCallbackBookingConfigDisabled)}
            </SecondaryCollapsibleSection>

            <SecondaryCollapsibleSection title="Test Drive" disabled={isTestDriveDisabled}>
                <div className={styles.fieldsLine}>
                    <CustomSwitchFieldWithTitle
                        textOnTrue={"Enabled"}
                        textOnFalse={"Disabled"}
                        title={"Enable test drive"}
                        description={"Enable customers to request or book test drives through assistant"}
                        valueSelector={(state) => state.toolSettings.updateRequest.requestsConfiguration.testDriveRequestsEnabled}
                        toggleValue={(v) => updateRequestsConfiguration({testDriveRequestsEnabled: v})}
                        disabled={isTestDriveDisabled}
                    />
                </div>
                <div className={styles.fieldsLine}>
                    <SelectDropdown<RequestMode>
                        title="Mode"
                        value={requestsConfiguration.testDriveMode}
                        handleChange={(value) => updateRequestsConfiguration({testDriveMode: value})}
                        options={modeOptions}
                        selectFirstOptionByDefault={false}
                        disabled={isTestDriveDisabled || !requestsConfiguration.testDriveRequestsEnabled}
                    />
                </div>
                {renderBookingConfig(BookingType.TestDrive, testDriveBookingConfig, isTestDriveBookingConfigDisabled)}
            </SecondaryCollapsibleSection>
        </Section>
    );
};
